import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem } from '../components/ui/carousel'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion'
import { Badge } from '../components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { FileText, Zap, Download, Shield, CheckCircle2, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_AUTH_REDIRECT = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/dashboard')}`;

const Logo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LandingPage = () => {
  const { login } = React.useContext(AuthContext);
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [api, setApi] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);

  // Handle Scroll for Navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Custom Autoplay using Embla API
  useEffect(() => {
    if (!api) return;

    const intervalId = setInterval(() => {
      api.scrollNext();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [api]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Honeypot check
    if (!isLogin && formData.confirm_email) {
      // Silently fail for bots
      setTimeout(() => {
        setLoading(false);
        toast.success('Account created successfully!');
        // Optionally redirect or close modal to simulate success
        setShowAuth(false);
      }, 1000);
      return;
    }

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);

      if (isLogin) {
        login(response.data.access_token, response.data.user);
        toast.success('Welcome back!');
      } else {
        toast.success(response.data.message || 'Account created! Please check your email to verify.');
        setIsLogin(true); // Switch to login tab
        // Optionally keep modal open or close it
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Authentication failed ${API}/auth/register, ${error}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-sans text-gray-900 bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white text-gray-900 shadow-md py-4' : 'bg-transparent text-white py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`p-1.5 rounded-lg ${isScrolled ? 'bg-blue-600' : 'bg-white/20 backdrop-blur-sm'}`}>
              <Logo className={`h-6 w-6 ${isScrolled ? 'text-white' : 'text-blue-400'}`} />
            </div>
            <span className={`text-2xl font-bold tracking-tight ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Quick Hire</span>
          </div>

          <div className={`hidden md:flex items-center space-x-8 ${isScrolled ? 'text-gray-600' : 'text-gray-200'}`}>
            <a href="/privacy-policy" className="hover:text-blue-500 transition-colors font-medium">Privacy Policy</a>
            <a href="/refund-policy" className="hover:text-blue-500 transition-colors font-medium">Refund Policy</a>
          </div>

          <Button
            onClick={() => setShowAuth(true)}
            className={`${isScrolled ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-white text-blue-900 hover:bg-gray-100'} rounded-full px-6 font-medium shadow-md transition-all`}
            data-testid="nav-get-started-btn"
          >
            Get Started
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative w-full h-screen overflow-hidden bg-gray-900 pt-0 mt-0">
        <Carousel
          setApi={setApi}
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full h-full"
        >
          <CarouselContent className="h-[100vh] -ml-0">
            {/* Slide 1 */}
            <CarouselItem className="h-full pl-0 min-w-full relative basis-full">
              <div className="absolute inset-0 w-full h-full">
                <img src="/assets/slider-1.png" alt="Office Team" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60" />
              </div>
              <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-center pt-20">
                <div className="max-w-3xl animate-in fade-in slide-in-from-bottom-10 duration-1000">
                  <Badge className="mb-6 px-4 py-1 text-sm font-medium bg-blue-500/20 text-blue-200 border-blue-500/30 backdrop-blur-md">
                    #1 Resume Builder
                  </Badge>
                  <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight">
                    Build Your Future in <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">Minutes</span>
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-200 mb-10 leading-relaxed font-light">
                    Create professional, ATS-friendly resumes that get you hired at top companies.
                  </p>
                  <Button
                    onClick={() => setShowAuth(true)}
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-10 py-7 text-xl font-semibold shadow-lg shadow-blue-600/30 transition-transform hover:-translate-y-1"
                  >
                    Start Building Free
                  </Button>
                </div>
              </div>
            </CarouselItem>

            {/* Slide 2 */}
            <CarouselItem className="h-full pl-0 min-w-full relative basis-full">
              <div className="absolute inset-0 w-full h-full">
                <img src="/assets/slider-2.png" alt="Resume Review" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60" />
              </div>
              <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-center pt-20">
                <div className="max-w-3xl animate-in fade-in slide-in-from-bottom-10 duration-1000">
                  <Badge className="mb-6 px-4 py-1 text-sm font-medium bg-green-500/20 text-green-200 border-green-500/30 backdrop-blur-md">
                    Professional Quality
                  </Badge>
                  <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight">
                    Polished. <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300">Professional.</span> Perfect.
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-200 mb-10 leading-relaxed font-light">
                    Choose from over 20+ premium templates designed by HR experts.
                  </p>
                  <Button
                    onClick={() => setShowAuth(true)}
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 text-white rounded-full px-10 py-7 text-xl font-semibold shadow-lg shadow-green-600/30 transition-transform hover:-translate-y-1"
                  >
                    View Templates
                  </Button>
                </div>
              </div>
            </CarouselItem>

            {/* Slide 3 */}
            <CarouselItem className="h-full pl-0 min-w-full relative basis-full">
              <div className="absolute inset-0 w-full h-full">
                <img src="/assets/slider-3.png" alt="Job Interview" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60" />
              </div>
              <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-center pt-20">
                <div className="max-w-3xl animate-in fade-in slide-in-from-bottom-10 duration-1000">
                  <Badge className="mb-6 px-4 py-1 text-sm font-medium bg-purple-500/20 text-purple-200 border-purple-500/30 backdrop-blur-md">
                    Success Stories
                  </Badge>
                  <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight">
                    Land Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-300">Dream Job</span>
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-200 mb-10 leading-relaxed font-light">
                    Join thousands of successful professionals who built their career with Quick Hire.
                  </p>
                  <Button
                    onClick={() => setShowAuth(true)}
                    size="lg"
                    className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-10 py-7 text-xl font-semibold shadow-lg shadow-purple-600/30 transition-transform hover:-translate-y-1"
                  >
                    Join Now
                  </Button>
                </div>
              </div>
            </CarouselItem>
          </CarouselContent>
        </Carousel>
      </section>

      {/* Features Section */}
      <section className="w-full py-24 bg-fixed bg-cover bg-center relative" style={{ backgroundImage: "url('/assets/feature-bg.png')" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-950/95 to-purple-950/95 backdrop-blur-sm"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-white mb-4">Why Choose Quick Hire?</h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">Everything you need to create a job-winning resume.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: FileText, title: "Modern Templates", desc: "Professionally crafted ATS-friendly designs.", color: "text-blue-600", bg: "bg-blue-50" },
              { icon: Zap, title: "AI-Powered", desc: "Smart suggestions to improve your content.", color: "text-yellow-600", bg: "bg-yellow-50" },
              { icon: Download, title: "Instant Export", desc: "Download in PDF or Word instantly.", color: "text-green-600", bg: "bg-green-50" },
              { icon: Shield, title: "Data Privacy", desc: "Your data is secure and never shared.", color: "text-purple-600", bg: "bg-purple-50" }
            ].map((feature, idx) => (
              <Card key={idx} className="p-8 text-center hover:shadow-2xl transition-all duration-300 border-none bg-white/95 backdrop-blur-md shadow-xl hover:-translate-y-2 group rounded-2xl">
                <div className={`w-16 h-16 ${feature.bg} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className={`h-8 w-8 ${feature.color}`} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="hiw" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-50"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 bg-purple-50 rounded-full blur-3xl opacity-50"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <Badge variant="outline" className="mb-4 text-blue-600 border-blue-200">Simple Process</Badge>
            <h2 className="text-4xl font-extrabold text-gray-900">How It Works</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12 relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gray-100 -z-10"></div>

            {[
              { step: "01", title: "Enter Your Details", desc: "Fill in your experience, skills, and education." },
              { step: "02", title: "Select a Template", desc: "Choose a design that matches your industry." },
              { step: "03", title: "Download & Apply", desc: "Get your polished resume and start applying." }
            ].map((item, idx) => (
              <div key={idx} className="text-center bg-white p-6 relative">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-blue-50 shadow-lg relative z-10">
                  <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-br from-blue-600 to-purple-600">{item.step}</span>
                </div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900">{item.title}</h3>
                <p className="text-gray-600 max-w-xs mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-gray-900 text-white relative overflow-hidden">
        {/* Abstract Background Shapes */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-600/20 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-600/20 rounded-full blur-[100px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <h2 className="text-4xl font-extrabold mb-6">Simple, Transparent Pricing</h2>
          <p className="text-xl text-gray-400 mb-16">No subscription traps. Pay only when you're satisfied.</p>

          <Card className="max-w-md mx-auto p-1 bg-gradient-to-b from-gray-700 to-gray-800 border-0 rounded-3xl shadow-2xl">
            <div className="bg-gray-900 rounded-[22px] p-10 h-full flex flex-col items-center">
              <div className="mb-4">
                <span className="text-gray-400 uppercase tracking-widest text-sm font-semibold">Per Download</span>
              </div>
              <div className="flex items-baseline mb-8">
                <span className="text-6xl font-black text-white">$20</span>
              </div>

              <ul className="text-left space-y-4 mb-10 w-full max-w-xs text-gray-300">
                {[
                  "Unlimited free previews",
                  "Access to all premium templates",
                  "High-quality PDF export",
                  "Secure data storage",
                  "24/7 Customer support"
                ].map((feature, i) => (
                  <li key={i} className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => setShowAuth(true)}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-full py-7 text-lg font-semibold shadow-lg shadow-blue-600/25"
                data-testid="pricing-get-started-btn"
              >
                Get Started Now
              </Button>
              <p className="mt-4 text-xs text-gray-500">100% Money-back guarantee if not satisfied.</p>
            </div>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600">Got questions? We've got answers.</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              { q: "How much does it cost?", a: "It costs just $20 to download your finalized resume. Creating and previewing is completely free." },
              { q: "Can I edit my resume after downloading?", a: "Yes, your data is saved in your dashboard. You can edit and re-download (standard fees apply for new downloads) at any time." },
              { q: "Is my personal information secure?", a: "Absolutely. We use bank-level encryption to store your data and never share it with third parties." },
              { q: "Can I create multiple resumes?", a: "Yes, you can create and manage unlimited different versions of your resume tailored to different job applications." }
            ].map((item, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="bg-white px-6 rounded-2xl border border-gray-100 shadow-sm data-[state=open]:shadow-md transition-all">
                <AccordionTrigger className="text-lg font-medium text-gray-900 hover:no-underline py-6">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-6 text-base leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Call To Action Banner */}
      <section id="cta" className="py-24 bg-fixed bg-cover bg-center relative overflow-hidden" style={{ backgroundImage: "url('/assets/cta-bg.png')" }}>
        <div className="absolute inset-0 bg-blue-900/80 backdrop-blur-sm"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10 px-6">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
            Ready to Accelerate Your Career?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Join thousands of professionals who have landed their dream jobs using Quick Hire.
          </p>
          <Button
            onClick={() => setShowAuth(true)}
            size="lg"
            className="bg-white text-blue-900 font-bold text-xl px-12 py-8 rounded-full hover:bg-gray-50 transition-all shadow-xl shadow-black/20 transform hover:-translate-y-1"
            data-testid="cta-banner-btn"
          >
            Build My Resume Now <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </div>
      </section>

      <Footer />

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
          <Card className="w-full max-w-md p-8 relative shadow-2xl border-0 bg-white/100">
            <button
              onClick={() => setShowAuth(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="close-auth-modal-btn"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center mb-8">
              <div className="bg-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-600/30 p-4">
                <Logo className="w-full h-full text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Quick Hire</h2>
              <p className="text-gray-500 mt-2">Sign in to continue building</p>
            </div>

            <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
              <TabsList className="grid w-full grid-cols-2 mb-8 bg-gray-100 p-1 rounded-xl">
                <TabsTrigger value="login" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="animate-in slide-in-from-left-2 duration-300">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="you@example.com"
                        className="h-11 bg-gray-50 border-gray-200"
                        data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label htmlFor="password">Password</Label>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!formData.email) {
                              toast.error("Please enter your email address in the field above first.");
                              return;
                            }
                            try {
                              setLoading(true);
                              await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
                              toast.success("Password reset link sent to your email.");
                            } catch (e) {
                              toast.error("Failed to send reset link.");
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        className="h-11 bg-gray-50 border-gray-200"
                        data-testid="login-password-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 text-base font-semibold shadow-lg shadow-blue-600/20"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? 'Logging in...' : 'Sign In'}
                    </Button>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="register" className="animate-in slide-in-from-right-2 duration-300">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot field - hidden from users */}
                  <div className="hidden">
                    <label htmlFor="confirm_email">Confirmation Email</label>
                    <input
                      type="text"
                      name="confirm_email"
                      id="confirm_email"
                      value={formData.confirm_email || ''}
                      onChange={handleInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="John Doe"
                      className="h-11 bg-gray-50 border-gray-200"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Email address</Label>
                    <Input
                      id="reg-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="you@example.com"
                      className="h-11 bg-gray-50 border-gray-200"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Password</Label>
                    <Input
                      id="reg-password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className="h-11 bg-gray-50 border-gray-200"
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 text-base font-semibold shadow-lg shadow-blue-600/20"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
